<!----
    Author: hjlee
    Description:
        파일 업로드 컨트롤
---->
<template>
  <div :class="['multifile-uploader', readonly ? 'readonly' : '', disabled ? 'disabled' : '']">
    <div
      :class="['file-preview-slide', selectedFile === undefined ? 'no-selected-file': 'file-selected', dragging ? 'dragging' : '']"
      @dragenter="dragEnter"
      @dragend="dragEnd"
      @dragleave="dragLeave"
      @dragover="handleDragOver($event)"
      @drop="handleDrop($event)"
    >
      <input
        ref="inputFileRef"
        type="file"
        :accept="accept"
        :class="{'file-control': true, 'clickable': !disabled && !readonly }"
        :multiple="multiple && maxFiles !== 1"
        :disabled="readonly || disabled"
        @change="onFileChangedFromControl"
        @click="onClickFileControl"
      >
      <img v-if="cameraButtonImage" :src="cameraButtonImage" class="camera-image" alt="Select file" @click="openCamera">
      <div v-if="selectedFile && !disabled && !noControlArea" class="file-control-container">
        <svg class="dimming" height="45" viewBox="0 0 500 200" preserveAspectRatio="none">
          <defs>
            <radialGradient id="gradient-10" cx=".5" cy="1.25" r="1.15">
              <stop offset="50%" stop-color="#000000" />
              <stop offset="56%" stop-color="#0a0a0a" />
              <stop offset="63%" stop-color="#262626" />
              <stop offset="69%" stop-color="#4f4f4f" />
              <stop offset="75%" stop-color="#808080" />
              <stop offset="81%" stop-color="#b1b1b1" />
              <stop offset="88%" stop-color="#dadada" />
              <stop offset="94%" stop-color="#f6f6f6" />
              <stop offset="100%" stop-color="#ffffff" />
            </radialGradient>
            <mask id="mask-10">
              <rect x="0" y="0" width="500" height="45" fill="url(#gradient-10)" />
            </mask>
          </defs>
          <rect x="0" width="500" height="150" fill="currentColor" mask="url(#mask-10)" />
        </svg>
        <div v-if="!noControlArea" class="file-control-area">
          <button v-if="!readonly && !noDeleteButton && showCircle" class="btn btn-sm delete-button-text btn-danger" @click="deleteFile">
            {{ $t('common.button.delete') }}
          </button>
          <div v-else-if="!readonly && !noDeleteButton" class="clickable delete-button" @click="deleteFile">
            x
          </div>
          <button v-if="!showCircle && !readonly" class="btn btn-light" @click="modifyFile">
            <i class="fa fa-pencil-square-o" aria-hidden="true" />
          </button>
          <button v-else-if="!readonly" class="btn btn-secondary btn-sm" @click="modifyFile">
            {{ $t('common.button.import') }}
          </button>
          <button v-if="!showCircle" class="btn btn-light" @click="downloadFile">
            <i class="fa fa-download" aria-hidden="true" /> {{ selectedFile.dwldCo }}
          </button>
          <button v-if="!showCircle && usePrimary && !readonly" class="btn btn-primary btn-set-primary" :disabled="selectedFile.primaryFlag === 'Y'" @click="setPrimary">
            <span class="button-primary-with-description">
              <i v-if="selectedFile.primaryFlag === 'Y'" class="fa fa-star" aria-hidden="true" />
              <i v-else class="fa fa-star-o" aria-hidden="true" /> {{ $t('file.text.mark_is_primary') }}</span>
            <span class="button-primary-without-description">
              <i v-if="selectedFile.primaryFlag === 'Y'" class="fa fa-star" aria-hidden="true" />
              <i v-else class="fa fa-star-o" aria-hidden="true" /></span>
          </button>
          <button v-else-if="!showCircle && usePrimary && selectedFile.primaryFlag === 'Y'" class="btn" :disabled="true">
            <span class="button-primary-with-description">
              <i class="fa fa-star" aria-hidden="true" />
            </span>
            <span class="button-primary-without-description">
              <i class="fa fa-star" aria-hidden="true" />
            </span>
          </button>
        </div>
      </div>
      <div
        class="dropping"
        @dragenter="dragEnter"
        @dragend="dragEnd"
        @dragleave="dragLeave"
        @dragover="handleDragOver($event)"
        @drop="handleDrop($event)"
      >
        <div
          class="dropping-line"
          @dragenter="dragEnter"
          @dragend="dragEnd"
          @dragleave="dragLeave"
          @dragover="handleDragOver($event)"
          @drop="handleDrop($event)"
        >
          <b-img
            src="~assets/img/add-white.png"
            :alt="$t('common.text.add')"
            @dragenter="dragEnter"
            @dragend="dragEnd"
            @dragleave="dragLeave"
            @dragover="handleDragOver($event)"
            @drop="handleDrop($event)"
          />
          <br>
          {{ $t('file.text.drop') }}
        </div>
      </div>
      <div v-if="selectedFile === undefined" class="selected-image-area">
        <div v-if="!noControlArea" class="file-control-area">
          <button v-if="!readonly && !noDeleteButton && showCircle" class="btn btn-sm delete-button-text btn-danger" :disabled="true">
            {{ $t('common.button.delete') }}
          </button>
          <button v-if="showCircle && !readonly" class="btn btn-secondary btn-sm" @click="modifyFile">
            {{ $t('common.button.import') }}
          </button>
        </div>
        <div v-if="isImage && (readonly || disabled)" class="upload-guide" v-html="$t('file.text.no_registered_image')" />
        <div v-else-if="isImage" class="upload-guide" v-html="$t('file.text.click')" />
        <div v-else-if="readonly || disabled" class="upload-guide" v-html="$t('file.text.no_registered_file')" />
        <div v-else class="upload-guide" v-html="$t('file.text.click')" />
      </div>
      <div v-else class="selected-image-area">
        <div class="selected-file-preview">
          <file-preview :name="selectedFile.fileNm ? selectedFile.fileNm : selectedFile.name" :file="selectedFile" scale="large" :cover="cover" />
        </div>
      </div>
    </div>
    <div class="clear" />
    <div v-if="multiple" class="thumb-area-container">
      <div class="thumb-area">
        <div v-for="(uploadFile, uploadFileIndex) in uploadFiles" :key="uploadFileIndex" :class="['thumb', !disabled?'clickable':'', uploadFile === selectedFile ? 'selected-file' : '', uploadFile.uploading ? 'uploading' : '']" @click="selectFile(uploadFile)">
          <b-progress v-if="uploadFile.uploading" :value="uploadFile.progress" variant="info" striped />
          <div v-if="usePrimary && uploadFile.primaryFlag === 'Y'" class="thumb-primary-icon">
            <i class="fa fa-star" aria-hidden="true" />
          </div>
          <file-preview :name="uploadFile.fileNm ? uploadFile.fileNm : uploadFile.name" :file="uploadFile" scale="small" />
        </div>
        <div v-if="showAddButton()" class="thumb thumb-add clickable" @click="addFile">
          <b-img
            src="~assets/img/add.png"
            :alt="$t('common.text.add')"
            @dragenter="dragEnter"
            @dragend="dragEnd"
            @dragleave="dragLeave"
            @dragover="handleDragOver($event)"
            @drop="handleDrop($event)"
          />
        </div>
      </div>
    </div>
    <image-viewer v-if="selectedFile && selectedFile.tmpId && preview" ref="imageViewerRef" :src="getImgUrl(selectedFile)" :alt="selectedFile.fileNm?selectedFile.fileNm:selectedFile.name" />
    <div class="clear" />
  </div>
</template>

<script>
export default {
  props: {
    name: {
      type: String,
      default: ''
    },
    showCircle: {
      type: Boolean,
      default: false
    },
    cameraButtonImage: {
      type: String,
      default: undefined
    },
    preview: {
      type: Boolean,
      default: true
    },
    cover: {
      type: Boolean,
      default: false
    },
    isImage: {
      type: Boolean,
      default: undefined
    },
    defaultImage: {
      type: String,
      default: undefined
    },
    customClickEvent: {
      type: String,
      default: undefined
    },
    value: {
      type: Array,
      default: undefined
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    maxFiles: {
      type: [Number, String],
      default: 5
    },
    multiple: {
      type: Boolean,
      default: true
    },
    accept: {
      type: String,
      default: ''
    },
    usePrimary: {
      type: Boolean,
      default: false
    },
    maxFileSize: {
      type: Number,
      default: 0
    },
    tableName: {
      type: String,
      default: ''
    },
    noDeleteButton: {
      type: Boolean,
      default: false
    },
    noControlArea: {
      type: Boolean,
      default: false
    },
    purpose: {
      type: String,
      default: ''
    }
  },
  data: function() {
    return {
      dragging: 0,
      selectedFile: undefined,
      uploadFiles: [],
      acceptExtensions: [],
      uploading: false
    }
  },
  watch: {
    value: function(newVal) {
      if (newVal) {
        this.uploadFiles = newVal
      }
      if (
        this.uploadFiles &&
        this.uploadFiles.length > 0 &&
        !this.selectedFile
      ) {
        this.selectedFile = this.uploadFiles[0]
      }
      if (!this.uploadFiles || this.uploadFiles.length === 0) {
        this.selectedFile = undefined
      }

      this.calculateThumbArea()
    }
  },
  mounted() {
    if (this.defaultImage) {
      $(this.$el)
        .find('.file-preview-slide.no-selected-file')
        .css('background-image', 'url(/img/' + this.defaultImage + ')')
    }
    this.calculateThumbArea()
    if (this.accept && this.accept !== '*') {
      this.acceptExtensions = this.accept.split(',')
    }
    if (this.value) {
      this.uploadFiles = this.value
    }
    if (this.uploadFiles && this.uploadFiles.length > 0) {
      this.selectedFile = this.uploadFiles[0]
    }
    this.calculateThumbArea()
  },
  methods: {
    addFile: function() {
      if (this.readonly || this.disabled) {
        return false
      }

      this.addClicked = true
      this.$refs.inputFileRef.click()
    },
    modifyFile: function() {
      if (this.readonly || this.disabled) {
        return false
      }
      this.addClicked = false
      this.modifyClicked = true
      this.$refs.inputFileRef.click()
    },
    downloadFile: function() {
      this.downloadFileWithTmpId(
        this.selectedFile.tmpId,
        this.selectedFile.fileNm
          ? this.selectedFile.fileNm
          : this.selectedFile.name,
        this.selectedFile
      )
    },
    openCamera(e) {
      if (this.readonly || this.disabled) {
        return false
      }
      if (e) {
        if (e.preventDefault) {
          e.preventDefault()
        }
        if (e.stopPropagation) {
          e.stopPropagation()
        }
      }
      this.vibrate(e)
      this.addClicked = false
      this.modifyClicked = true
      this.openCameraClicked = true
      if (window.app && window.app.setCameraDirect) {
        window.app.setCameraDirect(true)
      }
      this.$refs.inputFileRef.click()
    },
    onClickFileControl: function(e) {
      if (this.readonly || this.disabled) {
        return false
      }
      this.vibrate(e)
      if (this.openCameraClicked) {
        this.openCameraClicked = false
      } else if (window.app && window.app.setCameraDirect) {
        window.app.setCameraDirect(false)
      }
      if (e.isTrusted && this.selectedFile && this.preview) {
        if (this.isImage) {
          e.stopPropagation()
          e.preventDefault()
          this.showImageViewer()
        }
      } else if (this.modifyClicked) {
        this.addClicked = false
        this.modifyClicked = false
      } else {
        this.addClicked = true
      }
    },
    showImageViewer: function(e) {
      if (this.customClickEvent) {
        this.$emit('custom-click-event', {
          event: this.customClickEvent,
          file: this.selectedFile
        })
        if (e) {
          if (e.stopPropagation) {
            e.stopPropagation()
          }
          if (e.preventDefault) {
            e.preventDefault()
          }
        }
        return
      }

      this.$refs.imageViewerRef.showImageViewer()
    },
    dragEnter: function() {
      if (this.readonly || this.disabled) {
        return false
      }

      this.dragging++
    },
    dragEnd: function() {
      if (this.readonly || this.disabled) {
        return false
      }

      this.dragging--
      if (this.dragging < 0) {
        this.dragging = 0
      }
    },
    dragLeave: function() {
      if (this.readonly || this.disabled) {
        return false
      }

      this.dragging--
      if (this.dragging < 0) {
        this.dragging = 0
      }
    },
    handleDrop: function(e) {
      e.preventDefault()
      e.stopPropagation()
      if (this.readonly || this.disabled) {
        return false
      }

      this.addClicked = true
      this.dragging = 0
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        this.onFileChanged(e.dataTransfer.files)
      } else {
        this.$forceUpdate()
      }
    },
    handleDragOver: function(e) {
      e.preventDefault()
    },
    onFileChanged: function(files) {
      if (!files || files.length === 0) {
        return
      }
      if (this.readonly || this.disabled) {
        return false
      }
      if (!this.multiple) {
        this.selectedFile = undefined
        this.uploadFiles = []
      }

      let diff = 0
      if (!this.addClicked && this.selectedFile) {
        diff = -1
      }
      if (files.length + this.uploadFiles.length + diff > this.maxFiles) {
        console.error(
          this.$t('file.text.exceed_max_file_count', { cnt: this.maxFiles })
        )
        this.$toast.error(
          this.$t('file.text.exceed_max_file_count', { cnt: this.maxFiles })
        )
        this.$refs.inputFileRef.value = ''
        return
      }
      if (this.maxFileSize) {
        for (let i = 0; i < files.length; i++) {
          if (files[i].size > this.maxFileSize) {
            console.error(
              this.$t('file.error.not_supperted_size', {
                size: window.getFileSizeInString(this.maxFileSize),
                name: files[i].name
              })
            )
            this.$toast.error(
              this.$t('file.error.not_supperted_size', {
                size: window.getFileSizeInString(this.maxFileSize),
                name: files[i].name
              })
            )
            this.$refs.inputFileRef.value = ''
            return
          }
        }
      }
      if (this.acceptExtensions && this.acceptExtensions.length > 0) {
        for (let i = 0; i < files.length; i++) {
          let foundExtension = false
          for (let j = 0; j < this.acceptExtensions.length; j++) {
            if (
              files[i].name
                .toLowerCase()
                .endsWith(this.acceptExtensions[j].toLowerCase())
            ) {
              foundExtension = true
            }
          }
          if (!foundExtension) {
            console.error(
              this.$t('file.error.not_supperted_extension', {
                name: files[i].name
              })
            )
            this.$toast.error(
              this.$t('file.error.not_supperted_extension', {
                name: files[i].name
              })
            )
            this.$refs.inputFileRef.value = ''
            return
          }
        }
      }

      let replaceIndex = -1
      if (!this.addClicked && this.selectedFile) {
        for (let i = 0; i < this.uploadFiles.length; i++) {
          if (this.uploadFiles[i] === this.selectedFile) {
            replaceIndex = i
            this.uploadFiles.splice(i, 1)
            this.selectedFile = undefined
          }
        }
      }
      for (let i = 0; i < files.length; i++) {
        files[i].progress = 0
        files[i].uploading = true
        if (replaceIndex < 0) {
          this.uploadFiles.push(files[i])
        } else {
          this.uploadFiles.splice(replaceIndex, 0, files[i])
          replaceIndex = -1
        }
      }
      for (let i = 0; i < files.length; i++) {
        this.sendFile(files[i])
      }
      this.selectFile(files[0])
      this.calculateThumbArea()
      this.$refs.inputFileRef.value = ''
      if (this.usePrimary) {
        this.checkPrimary()
      }
      this.$forceUpdate()
    },
    sendFile(file) {
      if (this.readonly || this.disabled) {
        return false
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('tableName', this.tableName)
      formData.append('purpose', this.purpose)
      const self = this
      this.uploading = true
      this.$axios
        .$post(
          'file/insertTempFile?menuAlias=' + this.getMenuAlias(),
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            no_loading: true,
            onUploadProgress: progressEvent => {
              file.progress = (100 * progressEvent.loaded) / progressEvent.total
              self.$forceUpdate()
            }
          }
        )
        .then(response => {
          this.uploading = false
          file.uploading = false
          if (response.resultCode === 'success') {
            file.tmpId = response.data.tmpId
          } else {
            console.error(this.$t(response.message, response.params))
            this.$toast.error(this.$t(response.message, response.params))
            this.fileError(file)
          }

          this.updateValueToParent()
          this.$forceUpdate()
        })
        .catch(() => {
          file.uploading = false
          this.fileError(file)

          this.updateValueToParent()
          this.$forceUpdate()
        })
    },
    updateValueToParent() {
      if (this.uploadFiles && this.uploadFiles.length > 0) {
        for (let i = 0; i < this.uploadFiles.length; i++) {
          this.uploadFiles[i].fileOrdr = i + 1
        }
      }
      this.$emit('input', this.uploadFiles)
      this.$emit('change', this.uploadFiles)
    },
    fileError(file) {
      for (let i = 0; i < this.uploadFiles.length; i++) {
        if (this.uploadFiles[i] === file) {
          this.uploadFiles.splice(i, 1)
          if (this.selectedFile === file) {
            if (this.uploadFiles.length > 0) {
              this.selectFile(this.uploadFiles[0])
            } else {
              this.selectedFile = undefined
            }
          }
          this.$forceUpdate()
          break
        }
      }
      if (this.usePrimary) {
        this.checkPrimary()
      }
      this.calculateThumbArea()
    },
    onFileChangedFromControl: function(e) {
      if (this.readonly || this.disabled) {
        return false
      }
      this.$emit('changed', e)
      this.onFileChanged(e.target.files)
    },
    calculateThumbArea() {
      let width = this.uploadFiles.length * 70
      if (this.multiple && this.showAddButton()) {
        width += 70
      }
      $(this.$el)
        .find('.thumb-area')
        .width(width)
    },
    showAddButton() {
      if (this.readonly || this.disabled) {
        return false
      }
      return this.uploadFiles.length < this.maxFiles
    },
    deleteFile(e) {
      e.stopPropagation()
      e.preventDefault()
      if (this.readonly || this.disabled) {
        return false
      }
      if (!this.selectedFile) {
        return
      }

      for (let i = 0; i < this.uploadFiles.length; i++) {
        if (this.uploadFiles[i].tmpId === this.selectedFile.tmpId) {
          this.uploadFiles.splice(i, 1)
          if (this.uploadFiles.length > 0) {
            this.selectFile(this.uploadFiles[0])
          } else {
            this.selectedFile = undefined
          }

          this.updateValueToParent()
          this.$forceUpdate()
          break
        }
      }
      if (this.usePrimary) {
        this.checkPrimary()
      }
      this.calculateThumbArea()
    },
    checkPrimary() {
      if (!this.uploadFiles || this.uploadFiles.length === 0) {
        return
      }
      let foundPrimary = false
      for (let i = 0; i < this.uploadFiles.length; i++) {
        if (this.uploadFiles[i].primaryFlag === 'Y') {
          foundPrimary = true
          break
        }
      }
      if (!foundPrimary) {
        this.uploadFiles[0].primaryFlag = 'Y'
        this.$forceUpdate()
      }
    },
    setPrimary(e) {
      e.stopPropagation()
      e.preventDefault()
      if (!this.uploadFiles || this.uploadFiles.length === 0) {
        return
      }
      for (let i = 0; i < this.uploadFiles.length; i++) {
        if (this.uploadFiles[i] === this.selectedFile) {
          this.uploadFiles[i].primaryFlag = 'Y'
        } else {
          this.uploadFiles[i].primaryFlag = 'N'
        }
      }
      this.$forceUpdate()
    },
    isUploading() {
      if (!this.uploadFiles || this.uploadFiles.length === 0) {
        return false
      }
      for (let i = 0; i < this.uploadFiles.length; i++) {
        if (this.uploadFiles[i].uploading) {
          return true
        }
      }
      return false
    },
    selectFile(file) {
      if (this.disabled) return
      if (this.selectedFile === file) return
      this.selectedFile = file
      this.$forceUpdate()
    }
  }
}
</script>

<style scoped>
.multifile-uploader {
  margin-bottom: 0px;
  max-width: 100%;
  width: 100%;
  height: 321px;
}
.no-selected-file {
  background-image: url(~assets/img/empty-image-with-comment.png);
}
.file-selected {
  background-image: none !important;
}
.multifile-uploader.readonly .no-selected-file,
.multifile-uploader.disabled .no-selected-file {
  background-position-y: 60px;
}
.multifile-uploader.readonly .no-selected-file .upload-guide,
.multifile-uploader.disabled .no-selected-file .upload-guide {
  padding-top: 180px;
}
.multifile-uploader.disabled {
  opacity: 0.8;
  -webkit-filter: grayscale(100%); /* Safari 6.0 - 9.0 */
  filter: grayscale(100%);
}
.file-preview-slide {
  position: relative;
  width: 100%;
  height: 170px;
  background-repeat: no-repeat;
  background-color: #dddddd;
  background-position: center 10px;
}
.file-control {
  opacity: 0;
  position: absolute;
  width: 100%;
  height: 170px;
  z-index: 1;
}
.dropping {
  width: 100%;
  position: absolute;
  background-color: rgba(0, 0, 0, 0.7);
  opacity: 0;
  height: 0;
  transition: opacity 0.4s;
}
.selected-image-area {
  height: 100%;
}
.selected-file-preview {
  height: 100%;
}
.dragging .dropping {
  height: 270px;
  opacity: 1;
}
.dropping-line {
  margin: 20px;
  height: 130px;
  width: calc(100% - 40px);
  border: 2px dashed #888888;
  text-align: center;
  color: white;
}
.upload-guide {
  text-align: center;
  padding-top: 110px;
  margin: auto;
  color: #777777;
}
.thumb {
  width: 64px;
  height: 48px;
  float: left;
  margin: 3px;
  padding: 1px;
  border: 1px solid #aaaaaa;
  text-align: center;
  overflow: hidden;
}
.thumb.uploading {
  border: 2px solid #aa0000 !important;
  padding: 0px;
  opacity: 0.8;
}
.thumb >>> .progress {
  height: 7px;
  margin-bottom: -7px;
}
.thumb-area-container {
  margin: 0 -3px;
  text-align: center;
  height: 48px;
}
.thumb-area {
  margin: auto;
  height: 48px;
  max-width: 100%;
}
.thumb-add img {
  max-width: 100%;
  width: 40px;
}
.thumb-add {
  padding-top: 3px;
}
.file-control-area {
  z-index: 4;
  width: 100%;
  position: absolute;
}
.file-control-area button {
  float: right;
  margin: 5px;
}
.selected-file {
  padding: 0px;
  border: 2px solid #4444aa;
}
.thumb-primary-icon {
  position: absolute;
  margin-left: 5px;
}
.delete-button {
  margin: 5px;
  background-color: black;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 15px;
  border: 2px solid black;
  float: left;
  text-align: center;
  padding-top: 1px;
  line-height: 24px;
  font-family: sans-serif;
  font-weight: bold;
  font-size: 17px;
}
.delete-button:hover {
  border: 2px solid white;
}
.dimming {
  position: absolute;
  width: 100%;
}
.file-control-container {
  position: absolute;
  width: 100%;
  opacity: 0.3;
  z-index: 3;
  transition: opacity 0.4s;
}
.file-control-container:hover {
  opacity: 1;
}
.button-primary-with-description {
  display: none;
}
.button-primary-without-description {
  display: block;
}
.file-control-container:hover .button-primary-with-description {
  display: block;
}
.file-control-container:hover .button-primary-without-description {
  display: none;
}
.file-control-container .dimming {
  transition: opacity 0.4s;
  opacity: 0;
}
.btn-light {
  color: black;
}
.file-control-container:hover .dimming {
  opacity: 0.7;
}
.camera-image {
  position: absolute;
  left: calc(50% - 1.5rem);
  bottom: 0.2rem;
  width: 3rem;
  z-index: 2;
}
[path='/profile'] .file-preview-slide {
  width: calc(100vw - 3.33333333rem);
  height: calc(100vw + 3.33333333rem);
}
[path='/profile'] .empty-selected-file-preview .circle {
  border: 1px solid #dddddd;
  border-radius: 50%;
}
[path='/profile'] .selected-file-preview .circle {
  width: calc(100vw - 3.33333333rem);
  height: calc(100vw - 3.33333333rem);
}
[path='/profile'] .selected-file-preview {
  display: flex;
  align-items: center;
  justify-content: center;
}
[path='/profile'] .file-control {
  width: calc(100vw - 3.33333333rem);
  height: calc(100vw + 3.33333333rem);
}
[path='/profile'] .file-preview.circle {
  position: absolute;
  border-radius: 50%;
  border: 1px solid white;
}
[path='/profile'] .file-preview-slide {
  background-color: white;
}
[path='/profile'] .upload-guide {
  display: none;
}
.file-preview.circle-background {
  position: absolute;
  opacity: 0.3;
}
.no-open-on-click {
  width: 0 !important;
  height: 0 !important;
}
.delete-button-text {
  float: left !important;
}
</style>
